import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import * as SubframeCore from "@subframe/core";
import { FeatherSearch, FeatherX } from "@subframe/core";
import { DropdownMenu } from "../../../ui/components/DropdownMenu";
import { TextField } from "../../../ui/components/TextField";
import { Loader } from "../../../ui/components/Loader";
import {
  SearchableDropdownProps,
  GroupedItems,
  isGroupedItems,
} from "./SearchableDropdown.types";

/**
 * SearchableDropdown Component
 *
 * A reusable dropdown with integrated search functionality for filtering
 * dynamic API data locally. Supports both flat lists and grouped items.
 *
 * @example
 * // Simple usage
 * <SearchableDropdown
 *   items={customers}
 *   loading={loading}
 *   trigger={<Button>Select Customer</Button>}
 *   getSearchableText={(customer) => customer.customer_name}
 *   renderItem={(customer) => customer.customer_name}
 *   getItemKey={(customer) => customer.id}
 *   onSelect={handleSelect}
 * />
 *
 * @example
 * // With grouped items
 * <SearchableDropdown
 *   items={accountGroups}
 *   loading={loading}
 *   trigger={<Button>Select Account</Button>}
 *   getSearchableText={(account) => account.gl_name}
 *   renderItem={(account) => account.gl_name}
 *   getItemKey={(account) => account.account_id}
 *   onSelect={handleSelect}
 * />
 */
export function SearchableDropdown<T>({
  items: staticItems,
  loading: externalLoading = false,
  trigger,
  placeholder = "Select an option",
  emptyMessage = "No items available",
  noResultsMessage = "No results found",
  minSearchLength = 1,
  searchPlaceholder = "Search...",
  getSearchableText,
  renderItem,
  getItemKey,
  onSelect,
  selectedKey,
  disabled = false,
  className,
  dropdownClassName,
  maxHeight = "max-h-60",
  fetchItems,
  onLoadMore,
  hasMore: externalHasMore,
  initialLimit = 20,
}: SearchableDropdownProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  // Async fetching state
  const [fetchedItems, setFetchedItems] = useState<T[] | GroupedItems<T>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Focus search input when dropdown opens (after Radix menu focus runs)
  useLayoutEffect(() => {
    if (!isOpen) return;

    const focusSearch = () => {
      searchInputRef.current?.focus({ preventScroll: true });
    };

    focusSearch();
    const timeoutId = window.setTimeout(focusSearch, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      // Clear debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Reset pagination state when closing (but keep fetchedItems for better UX)
      if (fetchItems) {
        setCurrentOffset(0);
        setIsInitialLoad(true);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
        debouncedSearchTermRef.current = "";
        // Note: We don't clear fetchedItems here - it will be refreshed on next open
        // Currency changes are handled by cache clearing in parent components
      }
    }
  }, [isOpen, fetchItems]);

  // Force-close when disabled to prevent stale open state
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  // Fetch items when using async fetching
  const fetchItemsData = useCallback(
    async (search: string, offset: number, append: boolean = false) => {
      if (!fetchItems) return;
      if (isLoadingMoreRef.current && append) return; // Prevent multiple simultaneous loads

      if (append) {
        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else {
        setIsLoading(true);
      }

      try {
        const result = await fetchItems(search, initialLimit, offset);

        if (append) {
          // Use functional update to ensure we have the latest state
          setFetchedItems((prevItems) => {
            // If result is empty, don't replace existing items
            if (
              !result.items ||
              (Array.isArray(result.items) && result.items.length === 0)
            ) {
              return prevItems;
            }

            if (isGroupedItems(result.items) && isGroupedItems(prevItems)) {
              // Append to existing grouped items
              const existingGroups = prevItems;
              const newGroups = result.items;

              // Merge groups with same label
              const mergedGroups = [...existingGroups];
              newGroups.forEach((newGroup) => {
                const existingGroupIndex = mergedGroups.findIndex(
                  (g) => g.groupLabel === newGroup.groupLabel,
                );
                if (existingGroupIndex >= 0) {
                  // Remove duplicates before appending
                  const existingItems = mergedGroups[existingGroupIndex].items;
                  const existingIds = new Set(
                    existingItems.map((item: any) =>
                      getItemKey(item).toString(),
                    ),
                  );

                  // Filter out items that already exist
                  const newItemsToAdd = newGroup.items.filter((item: any) => {
                    const itemKey = getItemKey(item).toString();
                    return !existingIds.has(itemKey);
                  });

                  mergedGroups[existingGroupIndex] = {
                    ...mergedGroups[existingGroupIndex],
                    items: [...existingItems, ...newItemsToAdd],
                    hasMore: newGroup.hasMore,
                  };
                } else {
                  // New group, add it
                  mergedGroups.push(newGroup);
                }
              });
              return mergedGroups;
            } else if (
              !isGroupedItems(result.items) &&
              !isGroupedItems(prevItems)
            ) {
              // For flat items, also check for duplicates
              const existingIds = new Set(
                prevItems.map((item: any) => getItemKey(item).toString()),
              );
              const newItems = result.items.filter((item: any) => {
                const itemKey = getItemKey(item).toString();
                return !existingIds.has(itemKey);
              });
              return [...prevItems, ...newItems];
            }
            // If types don't match, replace
            return result.items;
          });
        } else {
          // Replace items (initial load or search change)
          // Only replace if we have items, otherwise keep previous (for error cases)
          if (
            result.items &&
            Array.isArray(result.items) &&
            result.items.length > 0
          ) {
            setFetchedItems(result.items);
          } else {
            // Empty result on initial load - set empty array
            setFetchedItems([]);
          }
        }

        // Simple: Check API's hasMore flag FIRST
        // If API says hasMore: false, stop pagination immediately
        if (result.hasMore === false) {
          setHasMore(false);
        } else if (isGroupedItems(result.items)) {
          // For grouped items, check if any group has more
          const hasMoreFromGroups = result.items.some((group) => group.hasMore);
          setHasMore(hasMoreFromGroups || result.hasMore || false);
        } else {
          setHasMore(result.hasMore || false);
        }
        setCurrentOffset(offset);
      } catch (error) {
        console.error("Error fetching items:", error);
        // On error, don't update items
      } finally {
        if (append) {
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        } else {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
    },
    [fetchItems, initialLimit, getItemKey],
  );

  // Fetch initial data when dropdown opens (async mode)
  useEffect(() => {
    if (isOpen && fetchItems && isInitialLoad) {
      fetchItemsData("", 0, false);
    }
  }, [isOpen, fetchItems, isInitialLoad, fetchItemsData]);

  // Debounce search term for API calls
  const debouncedSearchTermRef = useRef<string>("");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch on search term change (async mode, with debouncing)
  useEffect(() => {
    if (isOpen && fetchItems && !isInitialLoad) {
      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout to debounce the search
      debounceTimeoutRef.current = setTimeout(() => {
        const trimmedSearch = searchTerm.trim();
        // Only fetch if search term actually changed
        if (trimmedSearch !== debouncedSearchTermRef.current) {
          debouncedSearchTermRef.current = trimmedSearch;
          // Reset offset when search changes
          setCurrentOffset(0);
          fetchItemsData(trimmedSearch, 0, false);
        }
      }, 300); // 300ms debounce delay

      // Cleanup timeout on unmount or when dependencies change
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }
  }, [searchTerm, isOpen, fetchItems, isInitialLoad, fetchItemsData]);

  // Determine which items to use (static or fetched)
  const items = fetchItems ? fetchedItems : staticItems;

  /**
   * Filter items based on search term (only for static items mode)
   */
  const filteredItems = useMemo(() => {
    // For async fetching, items are already filtered by the API
    if (fetchItems) {
      // Ensure we return the items array directly
      if (!items) {
        return [];
      }
      // If items is already an array, return it as-is
      // This handles both flat arrays and grouped arrays
      return Array.isArray(items) ? items : [];
    }

    // For static items, filter locally
    if (!items) return [];

    const trimmedSearch = searchTerm.trim();

    // If search term is below minimum length, return all items
    if (trimmedSearch.length < minSearchLength) {
      return items;
    }

    const searchLower = trimmedSearch.toLowerCase();

    // Handle grouped items
    if (isGroupedItems(items)) {
      const filtered: GroupedItems<T>[] = [];

      items.forEach((group) => {
        const matchingItems = group.items.filter((item) =>
          getSearchableText(item).toLowerCase().includes(searchLower),
        );

        // Only include groups that have matching items
        if (matchingItems.length > 0) {
          filtered.push({
            groupLabel: group.groupLabel,
            items: matchingItems,
            hasMore: group.hasMore,
          });
        }
      });

      return filtered;
    }

    // Handle flat items
    return items.filter((item) =>
      getSearchableText(item).toLowerCase().includes(searchLower),
    );
  }, [items, searchTerm, minSearchLength, getSearchableText, fetchItems]);

  /**
   * Check if we have any items to display
   */
  const hasItems = useMemo(() => {
    if (!filteredItems) return false;
    if (!Array.isArray(filteredItems)) return false;
    if (filteredItems.length === 0) return false;

    // Check if it's grouped items
    if (isGroupedItems(filteredItems)) {
      // For grouped items, check if any group has items
      const hasAnyItems = filteredItems.some(
        (group) =>
          group &&
          group.items &&
          Array.isArray(group.items) &&
          group.items.length > 0,
      );
      return hasAnyItems;
    }

    // For flat items
    return filteredItems.length > 0;
  }, [filteredItems]);

  // Determine loading state
  const loading = fetchItems ? isLoading : externalLoading;

  // Determine hasMore state
  // Check main hasMore state FIRST - if false, stop pagination
  const hasMoreItems = fetchItems
    ? hasMore === false
      ? false
      : hasMore ||
        (isGroupedItems(filteredItems) && filteredItems.some((g) => g.hasMore))
    : externalHasMore || false;

  /**
   * Handle load more (triggered automatically on scroll)
   */
  const handleLoadMore = useCallback(() => {
    if (!fetchItems || isLoadingMoreRef.current) return;

    // Don't check threshold - rely on hasMoreItems flag from API
    // Client-side filtering (like currency) may reduce displayed items,
    // but API might still have more items that match the filter
    if (!hasMoreItems) {
      return;
    }

    if (onLoadMore) {
      onLoadMore(searchTerm.trim());
    } else {
      // Default load more behavior
      const nextOffset = currentOffset + initialLimit;
      fetchItemsData(searchTerm.trim(), nextOffset, true);
    }
  }, [
    fetchItems,
    onLoadMore,
    searchTerm,
    currentOffset,
    initialLimit,
    fetchItemsData,
    hasMoreItems,
  ]);

  // Infinite scroll: Observe when sentinel element is visible
  useEffect(() => {
    if (!isOpen || !fetchItems || !hasMoreItems || isLoading || isLoadingMore) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMoreRef.current) {
          handleLoadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "80px", // Start loading 80px before reaching bottom
        threshold: 0.1,
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    isOpen,
    fetchItems,
    hasMoreItems,
    isLoading,
    isLoadingMore,
    handleLoadMore,
  ]);

  /**
   * Handle item selection
   */
  const handleSelect = (item: T) => {
    onSelect(item);
    setIsOpen(false);
    setSearchTerm("");
  };

  /**
   * Handle keyboard navigation in search input
   */
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent dropdown from closing when typing
    e.stopPropagation();

    // Close dropdown on Escape
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  /**
   * Clear search input
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    searchInputRef.current?.focus();
  };

  /**
   * Render grouped items
   */
  const renderGroupedItems = (groups: GroupedItems<T>[]) => {
    return groups.map((group, groupIndex) => (
      <React.Fragment key={`group-${groupIndex}`}>
        {/* Group Header */}
        <div className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color bg-neutral-50 border-b border-solid border-neutral-100">
          {group.groupLabel}
        </div>

        {/* Group Items */}
        {group.items.map((item) => {
          const key = getItemKey(item);
          const isSelected = selectedKey !== undefined && selectedKey === key;

          return (
            <DropdownMenu.DropdownItem
              key={key}
              onClick={() => handleSelect(item)}
              className={
                isSelected
                  ? "bg-brand-50 border-l-4 border-l-brand-600 font-medium"
                  : "hover:bg-neutral-50"
              }
            >
              {renderItem(item)}
            </DropdownMenu.DropdownItem>
          );
        })}

        {/* Separator between groups (except for last one) */}
        {groupIndex < groups.length - 1 && (
          <div className="h-px bg-neutral-200 my-1" />
        )}
      </React.Fragment>
    ));
  };

  /**
   * Render flat items
   */
  const renderFlatItems = (flatItems: T[]) => {
    return flatItems.map((item) => {
      const key = getItemKey(item);
      const isSelected = selectedKey !== undefined && selectedKey === key;

      return (
        <DropdownMenu.DropdownItem
          key={key}
          onClick={() => handleSelect(item)}
          className={
            isSelected
              ? "bg-brand-50 border-l-4 border-l-brand-600 font-medium"
              : "hover:bg-neutral-50"
          }
        >
          {renderItem(item)}
        </DropdownMenu.DropdownItem>
      );
    });
  };

  return (
    <SubframeCore.DropdownMenu.Root
      open={isOpen && !disabled}
      onOpenChange={(open) => !disabled && setIsOpen(open)}
    >
      <SubframeCore.DropdownMenu.Trigger asChild={true}>
        {trigger}
      </SubframeCore.DropdownMenu.Trigger>

      <SubframeCore.DropdownMenu.Portal>
        <SubframeCore.DropdownMenu.Content
          side="bottom"
          align="start"
          sideOffset={4}
          asChild={true}
          className={className}
        >
          <div
            className={`flex flex-col min-w-[300px] rounded-md border border-solid border-neutral-border bg-default-background shadow-lg overflow-hidden searchable-dropdown-content ${
              dropdownClassName || ""
            }`}
            data-searchable-dropdown-content="true"
          >
            {/* Search Input */}
            <div className="sticky top-0 bg-default-background border-b border-solid border-neutral-border px-3 py-2 z-10">
              <div className="relative flex items-center">
                <div className="absolute left-3 flex items-center h-full pointer-events-none z-10">
                  <FeatherSearch className="text-body font-body text-subtext-color" />
                </div>
                <TextField label="" helpText="">
                  <TextField.Input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-10 pr-10"
                  />
                </TextField>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 flex items-center h-full text-subtext-color hover:text-default-font cursor-pointer"
                  >
                    <FeatherX className="text-body font-body" />
                  </button>
                )}
              </div>
            </div>

            {/* Dropdown Content */}
            <div
              ref={scrollContainerRef}
              className={`overflow-y-auto ${maxHeight} px-1 py-1`}
            >
              <div className="flex flex-col items-start">
                {loading ? (
                  <DropdownMenu.DropdownItem disabled>
                    <div className="flex items-center justify-center py-4 w-full">
                      <Loader />
                    </div>
                  </DropdownMenu.DropdownItem>
                ) : !hasItems && searchTerm.trim().length >= minSearchLength ? (
                  <DropdownMenu.DropdownItem disabled>
                    {noResultsMessage}
                  </DropdownMenu.DropdownItem>
                ) : !hasItems ? (
                  <DropdownMenu.DropdownItem disabled>
                    {emptyMessage}
                  </DropdownMenu.DropdownItem>
                ) : isGroupedItems(filteredItems) ? (
                  <>
                    {renderGroupedItems(filteredItems)}
                    {/* Loading more indicator */}
                    {isLoadingMore && (
                      <div className="flex items-center justify-center py-2 w-full">
                        <Loader />
                      </div>
                    )}
                    {/* Sentinel element for infinite scroll */}
                    {hasMoreItems && !isLoadingMore && (
                      <div ref={sentinelRef} className="h-1 w-full" />
                    )}
                  </>
                ) : (
                  <>
                    {renderFlatItems(filteredItems as T[])}
                    {/* Loading more indicator */}
                    {isLoadingMore && (
                      <div className="flex items-center justify-center py-2 w-full">
                        <Loader />
                      </div>
                    )}
                    {/* Sentinel element for infinite scroll */}
                    {hasMoreItems && !isLoadingMore && (
                      <div ref={sentinelRef} className="h-1 w-full" />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </SubframeCore.DropdownMenu.Content>
      </SubframeCore.DropdownMenu.Portal>
    </SubframeCore.DropdownMenu.Root>
  );
}

export default SearchableDropdown;
